import React, { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { DollarSignIcon, ShoppingBagIcon, UsersIcon, TrendingUpIcon, PlusIcon, BookOpenIcon, BarChart2Icon, CalendarIcon, PaletteIcon, BriefcaseIcon, PackageIcon, TruckIcon, CheckCircleIcon, TagIcon, StarIcon, LayoutDashboardIcon, ClockIcon, ArrowRightIcon, InfoIcon, ImageIcon } from 'lucide-react';
import { VendorFulfillmentDashboard } from './fulfillment/VendorFulfillmentDashboard';
type TabType = 'overview' | 'book' | 'merch' | 'event' | 'bookbox' | 'creative' | 'service' | 'fulfillment';
const VendorDashboard = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  // Tabs Configuration
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboardIcon,
    count: null
  }, {
    id: 'book',
    label: 'Books',
    icon: BookOpenIcon,
    count: 5
  }, {
    id: 'merch',
    label: 'Merch',
    icon: TagIcon,
    count: 12
  }, {
    id: 'event',
    label: 'Events',
    icon: CalendarIcon,
    count: 8
  }, {
    id: 'bookbox',
    label: 'Book Boxes',
    icon: PackageIcon,
    count: 2
  }, {
    id: 'creative',
    label: 'Creative',
    icon: PaletteIcon,
    count: 3
  }, {
    id: 'service',
    label: 'Services',
    icon: BriefcaseIcon,
    count: 2
  }, {
    id: 'fulfillment',
    label: 'Shipping',
    icon: TruckIcon,
    count: null
  }];
  // Stats Data
  const statsData = {
    overview: [{
      name: 'Total Revenue',
      value: '$18,750',
      change: '+14%',
      icon: DollarSignIcon,
      iconBg: 'bg-green-100',
      iconText: 'text-green-600'
    }, {
      name: 'Total Purchases',
      value: '1,245',
      change: '+8%',
      icon: ShoppingBagIcon,
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    }, {
      name: 'Followers',
      value: '3.8k',
      change: '+22%',
      icon: UsersIcon,
      iconBg: 'bg-purple-100',
      iconText: 'text-purple-600'
    }, {
      name: 'Total Reviews',
      value: '312',
      change: '+15%',
      icon: StarIcon,
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-600'
    }],
    book: [{
      name: 'Book Revenue',
      value: '$12,450',
      change: '+12%',
      icon: DollarSignIcon,
      iconBg: 'bg-green-100',
      iconText: 'text-green-600'
    }, {
      name: 'Books Sold',
      value: '845',
      change: '+5%',
      icon: ShoppingBagIcon,
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    }, {
      name: 'Page Reads (KU)',
      value: '45k',
      change: '+8%',
      icon: BookOpenIcon,
      iconBg: 'bg-orange-100',
      iconText: 'text-orange-600'
    }, {
      name: 'Book Reviews',
      value: '218 (★4.6)',
      change: '+10%',
      icon: StarIcon,
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-600'
    }],
    merch: [{
      name: 'Merch Revenue',
      value: '$3,580',
      change: '+15%',
      icon: DollarSignIcon,
      iconBg: 'bg-green-100',
      iconText: 'text-green-600'
    }, {
      name: 'Items Sold',
      value: '120',
      change: '+15%',
      icon: ShoppingBagIcon,
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    }, {
      name: 'Inventory',
      value: '450',
      change: '-2%',
      icon: PackageIcon,
      iconBg: 'bg-orange-100',
      iconText: 'text-orange-600'
    }, {
      name: 'Merch Reviews',
      value: '34 (★4.3)',
      change: '+6%',
      icon: StarIcon,
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-600'
    }],
    event: [{
      name: 'Event Revenue',
      value: '$1,800',
      change: '+25%',
      icon: DollarSignIcon,
      iconBg: 'bg-green-100',
      iconText: 'text-green-600'
    }, {
      name: 'Events Hosted',
      value: '12',
      change: '+2',
      icon: CalendarIcon,
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    }, {
      name: 'Total Attendees',
      value: '1.2k',
      change: '+20%',
      icon: UsersIcon,
      iconBg: 'bg-orange-100',
      iconText: 'text-orange-600'
    }, {
      name: 'Upcoming Events',
      value: '3',
      change: '',
      icon: ClockIcon,
      iconBg: 'bg-purple-100',
      iconText: 'text-purple-600'
    }],
    bookbox: [{
      name: 'Subscription Revenue',
      value: '$5,985',
      change: '+10%',
      icon: DollarSignIcon,
      iconBg: 'bg-green-100',
      iconText: 'text-green-600'
    }, {
      name: 'Active Subscribers',
      value: '150',
      change: '+10%',
      icon: UsersIcon,
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    }, {
      name: 'Boxes Shipped',
      value: '450',
      change: '+8%',
      icon: TruckIcon,
      iconBg: 'bg-orange-100',
      iconText: 'text-orange-600'
    }, {
      name: 'Subscriber Reviews',
      value: '28 (★4.7)',
      change: '+12%',
      icon: StarIcon,
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-600'
    }],
    creative: [{
      name: 'Creative Revenue',
      value: '$8,400',
      change: '+18%',
      icon: DollarSignIcon,
      iconBg: 'bg-green-100',
      iconText: 'text-green-600'
    }, {
      name: 'Active Projects',
      value: '4',
      change: '+1',
      icon: PaletteIcon,
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    }, {
      name: 'Completed Projects',
      value: '28',
      change: '+12%',
      icon: CheckCircleIcon,
      iconBg: 'bg-orange-100',
      iconText: 'text-orange-600'
    }, {
      name: 'Client Reviews',
      value: '22 (★4.9)',
      change: '+8%',
      icon: StarIcon,
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-600'
    }],
    service: [{
      name: 'Service Revenue',
      value: '$12,600',
      change: '+20%',
      icon: DollarSignIcon,
      iconBg: 'bg-green-100',
      iconText: 'text-green-600'
    }, {
      name: 'Active Clients',
      value: '8',
      change: '+2',
      icon: BriefcaseIcon,
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    }, {
      name: 'Projects Completed',
      value: '45',
      change: '+12%',
      icon: CheckCircleIcon,
      iconBg: 'bg-orange-100',
      iconText: 'text-orange-600'
    }, {
      name: 'Client Reviews',
      value: '38 (★4.8)',
      change: '+15%',
      icon: StarIcon,
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-600'
    }],
    fulfillment: []
  };
  // Top Performing Items Data
  const topItemsData = {
    overview: [{
      id: 1,
      name: 'The Silent Echo',
      type: 'book',
      metric: '124 sales',
      submetric: '★4.8 (42)',
      progress: 90,
      color: 'bg-purple-600',
      badgeColor: 'bg-purple-100 text-purple-800'
    }, {
      id: 2,
      name: 'Bookish & Spice',
      type: 'bookbox',
      metric: '95 subs',
      submetric: '★4.8 (18)',
      progress: 85,
      color: 'bg-rose-600',
      badgeColor: 'bg-rose-100 text-rose-800'
    }, {
      id: 3,
      name: 'Summer Book Signing',
      type: 'event',
      metric: '85 tickets',
      submetric: 'Sold Out',
      progress: 100,
      color: 'bg-indigo-600',
      badgeColor: 'bg-indigo-100 text-indigo-800'
    }],
    book: [{
      id: 1,
      name: 'The Silent Echo',
      type: 'book',
      metric: '124 sales',
      submetric: '42 reviews · ★4.8',
      progress: 90,
      color: 'bg-purple-600',
      badgeColor: 'bg-purple-100 text-purple-800'
    }, {
      id: 2,
      name: 'Midnight Whispers',
      type: 'book',
      metric: '98 sales',
      submetric: '31 reviews · ★4.5',
      progress: 75,
      color: 'bg-purple-600',
      badgeColor: 'bg-purple-100 text-purple-800'
    }, {
      id: 3,
      name: "Dragon's Crown",
      type: 'book',
      metric: '76 sales',
      submetric: '28 reviews · ★4.7',
      progress: 60,
      color: 'bg-purple-600',
      badgeColor: 'bg-purple-100 text-purple-800'
    }],
    merch: [{
      id: 1,
      name: 'Echo Series Hoodie',
      type: 'merch',
      metric: '45 sold',
      submetric: '12 reviews',
      progress: 80,
      color: 'bg-pink-600',
      badgeColor: 'bg-pink-100 text-pink-800'
    }, {
      id: 2,
      name: 'Dragon Enamel Pin',
      type: 'merch',
      metric: '32 sold',
      submetric: '8 reviews',
      progress: 65,
      color: 'bg-pink-600',
      badgeColor: 'bg-pink-100 text-pink-800'
    }, {
      id: 3,
      name: 'Bookish Tote Bag',
      type: 'merch',
      metric: '28 sold',
      submetric: '6 reviews',
      progress: 55,
      color: 'bg-pink-600',
      badgeColor: 'bg-pink-100 text-pink-800'
    }],
    event: [{
      id: 1,
      name: 'Summer Book Signing',
      type: 'event',
      metric: '85/100 tickets',
      submetric: 'Sold Out',
      progress: 100,
      color: 'bg-indigo-600',
      badgeColor: 'bg-indigo-100 text-indigo-800'
    }, {
      id: 2,
      name: 'Virtual Q&A',
      type: 'event',
      metric: '78/100 spots',
      submetric: 'Registering',
      progress: 78,
      color: 'bg-indigo-600',
      badgeColor: 'bg-indigo-100 text-indigo-800'
    }, {
      id: 3,
      name: 'Writing Workshop',
      type: 'event',
      metric: '22/25 spots',
      submetric: 'Limited',
      progress: 88,
      color: 'bg-indigo-600',
      badgeColor: 'bg-indigo-100 text-indigo-800'
    }],
    bookbox: [{
      id: 1,
      name: 'Bookish & Spice',
      type: 'bookbox',
      metric: '95 subs',
      submetric: '18 reviews · ★4.8',
      progress: 85,
      color: 'bg-rose-600',
      badgeColor: 'bg-rose-100 text-rose-800'
    }, {
      id: 2,
      name: 'Fantasy Realm Crate',
      type: 'bookbox',
      metric: '55 subs',
      submetric: '10 reviews · ★4.6',
      progress: 60,
      color: 'bg-rose-600',
      badgeColor: 'bg-rose-100 text-rose-800'
    }],
    creative: [{
      id: 1,
      name: 'eBook Cover Design',
      type: 'creative',
      metric: '12 projects',
      submetric: '★4.9',
      progress: 90,
      color: 'bg-violet-600',
      badgeColor: 'bg-violet-100 text-violet-800'
    }, {
      id: 2,
      name: 'Full Wrap Design',
      type: 'creative',
      metric: '8 projects',
      submetric: '★5.0',
      progress: 75,
      color: 'bg-violet-600',
      badgeColor: 'bg-violet-100 text-violet-800'
    }, {
      id: 3,
      name: 'Character Art',
      type: 'creative',
      metric: '5 projects',
      submetric: '★4.8',
      progress: 60,
      color: 'bg-violet-600',
      badgeColor: 'bg-violet-100 text-violet-800'
    }],
    service: [{
      id: 1,
      name: 'Developmental Edit',
      type: 'service',
      metric: '18 projects',
      submetric: '★4.9',
      progress: 90,
      color: 'bg-blue-600',
      badgeColor: 'bg-blue-100 text-blue-800'
    }, {
      id: 2,
      name: 'Copy Edit',
      type: 'service',
      metric: '15 projects',
      submetric: '★4.7',
      progress: 80,
      color: 'bg-blue-600',
      badgeColor: 'bg-blue-100 text-blue-800'
    }, {
      id: 3,
      name: 'Proofreading',
      type: 'service',
      metric: '12 projects',
      submetric: '★4.8',
      progress: 70,
      color: 'bg-blue-600',
      badgeColor: 'bg-blue-100 text-blue-800'
    }],
    fulfillment: []
  };
  // All Orders Data
  const allOrders = [{
    id: '#ORD-001',
    item: 'The Silent Echo',
    type: 'book',
    customer: 'Jane Doe',
    date: '2 hours ago',
    amount: '$24.99',
    status: 'Completed',
    typeColor: 'bg-purple-100 text-purple-800'
  }, {
    id: '#ORD-002',
    item: 'Echo Series Hoodie',
    type: 'merch',
    customer: 'John Smith',
    date: '5 hours ago',
    amount: '$29.99',
    status: 'Processing',
    typeColor: 'bg-pink-100 text-pink-800'
  }, {
    id: '#ORD-003',
    item: 'Book Signing Ticket',
    type: 'event',
    customer: 'Alice Johnson',
    date: '1 day ago',
    amount: '$15.00',
    status: 'Completed',
    typeColor: 'bg-indigo-100 text-indigo-800'
  }, {
    id: '#ORD-004',
    item: 'Bookish & Spice Box',
    type: 'bookbox',
    customer: 'Mike Brown',
    date: '1 day ago',
    amount: '$39.99',
    status: 'Shipped',
    typeColor: 'bg-rose-100 text-rose-800'
  }, {
    id: '#ORD-005',
    item: 'eBook Cover Design',
    type: 'creative',
    customer: 'Sarah Wilson',
    date: '2 days ago',
    amount: '$250.00',
    status: 'In Progress',
    typeColor: 'bg-violet-100 text-violet-800'
  }, {
    id: '#ORD-006',
    item: 'Developmental Edit',
    type: 'service',
    customer: 'David Lee',
    date: '3 days ago',
    amount: '$450.00',
    status: 'Pending',
    typeColor: 'bg-blue-100 text-blue-800'
  }, {
    id: '#ORD-007',
    item: 'Midnight Whispers',
    type: 'book',
    customer: 'Emma Davis',
    date: '3 days ago',
    amount: '$19.99',
    status: 'Completed',
    typeColor: 'bg-purple-100 text-purple-800'
  }, {
    id: '#ORD-008',
    item: 'Dragon Enamel Pin',
    type: 'merch',
    customer: 'Chris Taylor',
    date: '4 days ago',
    amount: '$12.99',
    status: 'Completed',
    typeColor: 'bg-pink-100 text-pink-800'
  }];
  // Filter orders based on active tab
  const filteredOrders = activeTab === 'overview' ? allOrders : allOrders.filter(order => order.type === activeTab);
  // Quick Actions Data
  const quickActionsData = {
    overview: [{
      label: 'Add New Book',
      icon: BookOpenIcon,
      link: '/vendors/manage?type=book',
      bg: 'bg-purple-100',
      color: 'text-purple-600'
    }, {
      label: 'Add Product',
      icon: TagIcon,
      link: '/vendors/manage?type=merch',
      bg: 'bg-pink-100',
      color: 'text-pink-600'
    }, {
      label: 'Create Event',
      icon: CalendarIcon,
      link: '/vendors/manage?type=event',
      bg: 'bg-indigo-100',
      color: 'text-indigo-600'
    }, {
      label: 'Add Book Box',
      icon: PackageIcon,
      link: '/vendors/manage?type=bookbox',
      bg: 'bg-rose-100',
      color: 'text-rose-600'
    }],
    book: [{
      label: 'Add New Book',
      icon: PlusIcon,
      link: '/vendors/manage?type=book',
      bg: 'bg-purple-100',
      color: 'text-purple-600'
    }, {
      label: 'View Bookshelf',
      icon: BookOpenIcon,
      link: '/authors/bookshelf',
      bg: 'bg-purple-100',
      color: 'text-purple-600'
    }, {
      label: 'Manage ARCs',
      icon: UsersIcon,
      link: '/vendors/arc',
      bg: 'bg-purple-100',
      color: 'text-purple-600'
    }, {
      label: 'Pre-Orders',
      icon: ClockIcon,
      link: '/vendors/preorders',
      bg: 'bg-purple-100',
      color: 'text-purple-600'
    }],
    merch: [{
      label: 'Add New Product',
      icon: PlusIcon,
      link: '/vendors/manage?type=merch',
      bg: 'bg-pink-100',
      color: 'text-pink-600'
    }, {
      label: 'View Inventory',
      icon: PackageIcon,
      link: '/manage-listings',
      bg: 'bg-pink-100',
      color: 'text-pink-600'
    }, {
      label: 'Marketing Tools',
      icon: TrendingUpIcon,
      link: '/vendors/marketing',
      bg: 'bg-pink-100',
      color: 'text-pink-600'
    }],
    event: [{
      label: 'Create Event',
      icon: PlusIcon,
      link: '/vendors/manage?type=event',
      bg: 'bg-indigo-100',
      color: 'text-indigo-600'
    }, {
      label: 'Manage Showings',
      icon: CalendarIcon,
      link: '/manage-events',
      bg: 'bg-indigo-100',
      color: 'text-indigo-600'
    }, {
      label: 'Saved Events',
      icon: StarIcon,
      link: '/saved-events',
      bg: 'bg-indigo-100',
      color: 'text-indigo-600'
    }],
    bookbox: [{
      label: 'Add Book Box',
      icon: PlusIcon,
      link: '/vendors/manage?type=bookbox',
      bg: 'bg-rose-100',
      color: 'text-rose-600'
    }, {
      label: 'Manage Subs',
      icon: UsersIcon,
      link: '/manage-listings',
      bg: 'bg-rose-100',
      color: 'text-rose-600'
    }],
    creative: [{
      label: 'Edit Profile',
      icon: PaletteIcon,
      link: '/vendors/manage?type=creative',
      bg: 'bg-violet-100',
      color: 'text-violet-600'
    }, {
      label: 'View Portfolio',
      icon: ImageIcon,
      link: '/creative/cover-gallery',
      bg: 'bg-violet-100',
      color: 'text-violet-600'
    }, {
      label: 'Marketing',
      icon: TrendingUpIcon,
      link: '/vendors/marketing',
      bg: 'bg-violet-100',
      color: 'text-violet-600'
    }],
    service: [{
      label: 'Edit Profile',
      icon: BriefcaseIcon,
      link: '/vendors/manage?type=service',
      bg: 'bg-blue-100',
      color: 'text-blue-600'
    }, {
      label: 'View Clients',
      icon: UsersIcon,
      link: '/orders',
      bg: 'bg-blue-100',
      color: 'text-blue-600'
    }, {
      label: 'Marketing',
      icon: TrendingUpIcon,
      link: '/vendors/marketing',
      bg: 'bg-blue-100',
      color: 'text-blue-600'
    }],
    fulfillment: []
  };
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-21623" data-mp="1cmlpfxjaxkd9">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21624" data-mp="17n8sgjypuwwy">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4" data-id="element-21625" data-mp="1lmrxv5057u5k">
          <div data-id="element-21626" data-mp="1hlessx4vjuzp">
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-21627" data-mp="1hkwza89ljuvh">
              Vendor Dashboard
            </h1>
            <p className={`text-sm mt-1 ${dk.textMuted}`} data-id="element-21628" data-mp="1vjwdelzhgrde">
              Welcome back, <span className="font-medium" data-id="element-21629" data-mp="1qmcxlhn506jc">Your Store</span>
            </p>
          </div>
          <div className="flex space-x-3 w-full md:w-auto" data-id="element-21630" data-mp="25ahhuq1u8q48">
            <Link to="/vendors/analytics" className={`flex-1 md:flex-none justify-center flex items-center px-4 py-2 border rounded-md transition-colors ${dk.card}`} data-id="element-21631" data-mp="259xxz3smnrnk">
              <BarChart2Icon className="h-4 w-4 mr-2" data-id="element-21632" data-mp="2e9cryd806kct" />
              View Analytics
            </Link>
            <Link to="/vendors/manage" className={`flex-1 md:flex-none justify-center flex items-center px-4 py-2 rounded-md transition-colors text-white ${rt.primaryButton}`} data-id="element-21633" data-mp="29ahwobsun3bt">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-21634" data-mp="2vt07yo6au8t8" />
              Add New Listing
            </Link>
          </div>
        </div>

        {/* Type Tabs */}
        <div className="mb-8 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-21635" data-mp="2quowya49ps9f">
          <div className="flex space-x-2" data-id="element-21636" data-mp="235mifb4xkfj5">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as TabType)} className={`flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeTab === tab.id ? `text-white shadow-md ${rt.bgDark}` : `border ${dk.card}`}`} data-id="element-21637" data-mp="1y603mx9ger88">
                <tab.icon className={`h-4 w-4 mr-2 ${activeTab === tab.id ? 'text-white' : dk.textFaint}`} data-id="element-21638" data-mp="1kcimtk85jslw" />
                {tab.label}
                {tab.count !== null && <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20 text-white' : dk.badgeNeutral}`} data-id="element-21639" data-mp="3r0965bsk3nu8">
                    {tab.count}
                  </span>}
              </button>)}
          </div>
        </div>

        {activeTab === 'fulfillment' ? <VendorFulfillmentDashboard vendorCategory="merch" data-id="element-21640" data-mp="dor07mfx6bul" /> : <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-21641" data-mp="ilr3jpldx0i0">
              {statsData[activeTab].map((stat, index) => <div key={index} className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-21642" data-mp="9lcfwjky79bc" data-mpi={index}>
                  <div className="flex items-center justify-between" data-id="element-21643" data-mp="2pbowzgt8s3mo">
                    <div data-id="element-21644" data-mp="2ud4i18vf6a52">
                      <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-21645" data-mp="2udeb55brgxez">
                        {stat.name}
                      </p>
                      <p className={`text-2xl font-bold mt-1 ${dk.textPrimary}`} data-id="element-21646" data-mp="ux1of2j8ilw1">
                        {stat.value}
                      </p>
                    </div>
                    <div className={`p-3 rounded-full ${stat.iconBg} ${stat.iconText}`} data-id="element-21647" data-mp="m59hh1tf5uw7">
                      <stat.icon className="h-6 w-6" data-id="element-21648" data-mp="h6y62mnh7cbv" />
                    </div>
                  </div>
                  {stat.change && <div className="mt-4 flex items-center text-sm" data-id="element-21649" data-mp="777easqavbfh">
                      <TrendingUpIcon className={`h-4 w-4 mr-1 ${stat.change.startsWith('-') ? 'text-red-500' : 'text-green-500'}`} data-id="element-21650" data-mp="77h34czlzcjl" />
                      <span className={`font-medium ${stat.change.startsWith('-') ? 'text-red-500' : 'text-green-500'}`} data-id="element-21651" data-mp="21jx34w1t9ug0">
                        {stat.change}
                      </span>
                      <span className={`ml-2 ${dk.textMuted}`} data-id="element-21652" data-mp="3fff9km0b0m1q">
                        from last month
                      </span>
                    </div>}
                </div>)}
            </div>

            {/* Overview Summary Bar */}
            {activeTab === 'overview' && <div className={`mb-8 p-4 rounded-lg border ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`} data-id="element-21653" data-mp="2winl7y5wngqt">
                <div className={`flex flex-wrap items-center justify-center gap-2 text-sm ${dk.textMuted}`} data-id="element-21654" data-mp="31jl2m8j3c6r9">
                  <span className={`font-medium ${dk.textSecondary}`} data-id="element-21655" data-mp="3aif4adba3nbo">
                    Active Listings:
                  </span>
                  {[{
              label: '5 Books',
              tab: 'book'
            }, {
              label: '12 Merch Items',
              tab: 'merch'
            }, {
              label: '8 Events',
              tab: 'event'
            }, {
              label: '2 Book Boxes',
              tab: 'bookbox'
            }, {
              label: '3 Creative Services',
              tab: 'creative'
            }, {
              label: '2 Service Offerings',
              tab: 'service'
            }].map(({
              label,
              tab
            }, i, arr) => <Fragment key={tab} data-id="element-21656" data-mp="1c0lfpzocx01d" data-mpi={i}>
                      <button onClick={() => setActiveTab(tab as TabType)} className={`hover:${rt.textPrimary} transition-colors`} data-id="element-21657" data-mp="1c1o2pwik6ic0">
                        {label}
                      </button>
                      {i < arr.length - 1 && <span data-id="element-21658" data-mp="24nuy0xoix919">•</span>}
                    </Fragment>)}
                </div>
              </div>}

            {/* Queue Status Banner */}
            {(activeTab === 'creative' || activeTab === 'service') && <div className={`mb-8 p-4 rounded-lg border flex items-center justify-between ${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-100'}`} data-id="element-21659" data-mp="1znqd8vgl1s58">
                <div className="flex items-center" data-id="element-21660" data-mp="1qn1z80r6vrsc">
                  <InfoIcon className="h-5 w-5 text-blue-500 mr-3" data-id="element-21661" data-mp="1qomtdp9ld8po" />
                  <div data-id="element-21662" data-mp="1byxdenwub05c">
                    <p className={`font-medium ${darkMode ? 'text-blue-300' : 'text-blue-800'}`} data-id="element-21663" data-mp="1bzgu61ar7ihs">
                      Queue Status: Open{' '}
                      <span className="opacity-75 font-normal" data-id="element-21664" data-mp="12yirbmc1xh0s">
                        ({activeTab === 'creative' ? '3/5' : '4/6'} slots
                        filled)
                      </span>
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} data-id="element-21665" data-mp="39nb22sxs4bd0">
                      Estimated wait time:{' '}
                      {activeTab === 'creative' ? '2 weeks' : '2-3 weeks'}
                    </p>
                  </div>
                </div>
                <button className={`text-sm font-medium px-3 py-1.5 rounded-md ${darkMode ? 'bg-blue-800 text-blue-100 hover:bg-blue-700' : 'bg-white text-blue-600 border border-blue-200 hover:bg-blue-50'}`} data-id="element-21666" data-mp="2vsifjajh1uwx">
                  Manage Queue
                </button>
              </div>}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-21667" data-mp="3elm9yy4ikbsl">
              {/* Revenue Chart */}
              <div className={`lg:col-span-2 p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-21668" data-mp="285sbk3nkbq9o">
                <div className="flex justify-between items-center mb-6" data-id="element-21669" data-mp="234322sfznknp">
                  <h2 className={`text-lg font-bold ${dk.textPrimary}`} data-id="element-21670" data-mp="ioog2lyiof9e">
                    Revenue Overview
                  </h2>
                  <select className={`text-sm border rounded-md px-2 py-1 focus:outline-none ${dk.inputOnCard}`} data-id="element-21671" data-mp="nmyagucf3bty">
                    <option data-id="element-21672" data-mp="wfa0wg7j6z9x">Last 30 Days</option>
                    <option data-id="element-21673" data-mp="wh4mlwqmmvf2">Last 3 Months</option>
                    <option data-id="element-21674" data-mp="11eweiqr9uhj9">This Year</option>
                  </select>
                </div>
                <div className={`h-64 flex items-center justify-center rounded-lg border border-dashed relative overflow-hidden group ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'}`} data-id="element-21675" data-mp="16coem822l9yj">
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-21676" data-mp="1fd31vcylgz4o">
                    <p className={`font-medium z-10 ${dk.textMuted}`} data-id="element-21677" data-mp="3d58hk4gdp2o2">
                      Chart Visualization Placeholder
                    </p>
                  </div>
                  <div className="flex items-end justify-between w-full h-full px-8 pb-4 opacity-30 gap-2" data-id="element-21678" data-mp="2y879uf3jzijd">
                    {[40, 65, 45, 80, 55, 70, 90, 60, 75, 50, 85, 95].map((h, i) => <div key={i} className={`w-full rounded-t-sm transition-all duration-500 ${rt.bgDark}`} style={{
                  height: `${h}%`
                }} data-id="element-21679" data-mp="370sr1qrjdh63" data-mpi={i} />)}
                  </div>
                </div>
              </div>

              {/* Top Performing Items */}
              <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-21680" data-mp="2jbzqti3vsn5o">
                <h2 className={`text-lg font-bold mb-4 ${dk.textPrimary}`} data-id="element-21681" data-mp="2je2rcw2ntyon">
                  Top Performing
                </h2>
                <div className="space-y-6" data-id="element-21682" data-mp="3ureneks6hd09">
                  {topItemsData[activeTab]?.map(item => <div key={item.id} className="flex items-center" data-id="element-21683" data-mp="71zuo6rvh17j">
                      <div className="flex-1" data-id="element-21684" data-mp="b44ob5iblxu6">
                        <div className="flex justify-between items-center mb-1" data-id="element-21685" data-mp="b4w8zladqb5o">
                          <h3 className={`text-sm font-medium truncate pr-2 ${dk.textPrimary}`} data-id="element-21686" data-mp="3taj1h28rm3ri">
                            {item.name}
                          </h3>
                          <span className={`text-[10px] px-1.5 py-0.5 ${rt.badgeRadius} uppercase font-bold tracking-wide ${item.badgeColor}`} data-id="element-21687" data-mp="pubw8rapbygm">
                            {item.type}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mb-2" data-id="element-21688" data-mp="l0zjbefdxg3p">
                          <p className={`text-xs ${dk.textMuted}`} data-id="element-21689" data-mp="18qtwxy7j38p0">
                            {item.metric}
                          </p>
                          <p className={`text-xs ${dk.textMuted}`} data-id="element-21690" data-mp="1dkod4rnxd5jc">
                            {item.submetric}
                          </p>
                        </div>
                        <div className={`w-full rounded-full h-1.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-21691" data-mp="2kge358k3rans">
                          <div className={`${item.color} h-1.5 rounded-full`} style={{
                      width: `${item.progress}%`
                    }} data-id="element-21692" data-mp="2u80d71kpf3oy" />
                        </div>
                      </div>
                    </div>)}
                </div>
                <Link to="/manage-listings" className={`block mt-6 text-center text-sm font-medium flex items-center justify-center ${rt.accentLink}`} data-id="element-21693" data-mp="2ajj8x1uv3wfb">
                  View All Listings
                  <ArrowRightIcon className="h-3 w-3 ml-1" data-id="element-21694" data-mp="3lxmx1rerqqcg" />
                </Link>
              </div>
            </div>

            {/* Recent Orders */}
            <div className="mb-8" data-id="element-21695" data-mp="169psfrsfcque">
              <div className="flex justify-between items-center mb-4" data-id="element-21696" data-mp="16ask41xkmxbd">
                <h2 className={`text-lg font-bold ${dk.textPrimary}`} data-id="element-21697" data-mp="11irdavj69ysq">
                  Recent Activity
                </h2>
                <Link to="/orders" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-21698" data-mp="wiv7cqqwzxaj">
                  View All Orders
                </Link>
              </div>
              <div className={`rounded-xl shadow-sm overflow-hidden border ${dk.card}`} data-id="element-21699" data-mp="mkyunuibwuab">
                <div className="overflow-x-auto" data-id="element-21700" data-mp="im9bflam0iir">
                  <table className={`min-w-full divide-y ${dk.divider}`} data-id="element-21701" data-mp="dlu0t1hppp4h">
                    <thead className={darkMode ? 'bg-gray-750' : 'bg-gray-50'} data-id="element-21702" data-mp="2cy6ao0famsod">
                      <tr data-id="element-21703" data-mp="2cvtsbm5migas">
                        {['Order ID', 'Item', 'Type', 'Customer', 'Date', 'Amount', 'Status'].map(h => <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-21704" data-mp="2i3t94qad7ymq">
                            {h}
                          </th>)}
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${dk.divider}`} data-id="element-21705" data-mp="30trprus8b01z">
                      {filteredOrders.length > 0 ? filteredOrders.map(order => <tr key={order.id} data-id="element-21706" data-mp="35ufeuawwd6tk">
                            <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${rt.textPrimary}`} data-id="element-21707" data-mp="1pdb6fqdjzz9u">
                              {order.id}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textPrimary}`} data-id="element-21708" data-mp="1mw10f70sexhq">
                              {order.item}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-21709" data-mp="1qv04kpep2q84">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold ${rt.badgeRadius} ${order.typeColor}`} data-id="element-21710" data-mp="1qtow4cab0uao">
                                {order.type}
                              </span>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textPrimary}`} data-id="element-21711" data-mp="185a87xva9we3">
                              {order.customer}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textMuted}`} data-id="element-21712" data-mp="ffl8dnub82e2">
                              {order.date}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${dk.textPrimary}`} data-id="element-21713" data-mp="xztuc3mfcate">
                              {order.amount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-21714" data-mp="1bsjhibdovls3">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold ${rt.badgeRadius} ${order.status === 'Completed' || order.status === 'Shipped' ? 'bg-green-100 text-green-800' : order.status === 'Processing' || order.status === 'In Progress' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-21715" data-mp="1gse8sg1dmddo">
                                {order.status}
                              </span>
                            </td>
                          </tr>) : <tr data-id="element-21716" data-mp="24vubqcmq01bj">
                          <td colSpan={7} className={`px-6 py-8 text-center text-sm ${dk.textMuted}`} data-id="element-21717" data-mp="24vkkklpxvo49">
                            No recent activity found for this tab.
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div data-id="element-21718" data-mp="2jkh2dahssfi2">
              <h2 className={`text-lg font-bold mb-4 ${dk.textPrimary}`} data-id="element-21719" data-mp="2jjyvljxieivw">
                Quick Actions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-21720" data-mp="2elxgfa37f3pp">
                {quickActionsData[activeTab]?.map((action, idx) => <Link key={idx} to={action.link} className={`flex items-center p-4 rounded-xl border transition-all hover:shadow-md ${dk.card}`} data-id="element-21721" data-mp="135z2osx2zti5">
                    <div className={`p-3 rounded-full mr-4 ${action.bg}`} data-id="element-21722" data-mp="19mh7p1j07d7m">
                      <action.icon className={`h-6 w-6 ${action.color}`} data-id="element-21723" data-mp="vmpqkrx6xgpe" />
                    </div>
                    <div data-id="element-21724" data-mp="10l9akms84if4">
                      <h3 className={`font-medium ${dk.textPrimary}`} data-id="element-21725" data-mp="10oosryh727kr">
                        {action.label}
                      </h3>
                      <p className={`text-xs mt-0.5 ${dk.textMuted}`} data-id="element-21726" data-mp="qoxzg04ddym9">
                        View & Edit
                      </p>
                    </div>
                  </Link>)}
                <Link to="/vendors/manage" className={`flex items-center p-4 rounded-xl border border-dashed transition-all hover:shadow-md ${darkMode ? 'bg-gray-800/50 border-gray-700 hover:border-gray-500' : 'bg-gray-50 border-gray-300 hover:border-gray-400'}`} data-id="element-21727" data-mp="gy3agnzqokx4">
                  <div className={`p-3 rounded-full mr-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-21728" data-mp="3kikb55bgr32q">
                    <PlusIcon className={`h-6 w-6 ${dk.textFaint}`} data-id="element-21729" data-mp="3fkipccmnkpnk" />
                  </div>
                  <div data-id="element-21730" data-mp="3udlypwq3bx37">
                    <h3 className={`font-medium ${dk.textSecondary}`} data-id="element-21731" data-mp="3pglw5vsg9cgu">
                      Add New Listing
                    </h3>
                    <p className={`text-xs mt-0.5 ${dk.textMuted}`} data-id="element-21732" data-mp="79xpi1hr4h92">
                      Any product type
                    </p>
                  </div>
                </Link>
              </div>
            </div>
          </>}
      </div>
    </div>;
};
export default VendorDashboard;